import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import io from 'socket.io-client';
import Clarity from '@microsoft/clarity';
import { NotificationInterface } from '../../../../Models/Notification';
import { SessionManager } from '../../../../services/SessionManager/SessionManager';
import { BuyerAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/BuyerAPIHandler';
import { NotificationAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/NotificationAPIHandler';
import { FeedbackAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/FeedbackAPIHandler';
import { ServerConfig } from '../../../../services/NetworkClient/ServerConfig';
import { RootState } from '../../../../redux/store';
import { resetPanelClosed, triggerNewNotification } from '../../../PageComponents/NotificationWindow/NotificationSlice';
import { closeContactUsModal } from '../../../../redux/ContactUsModalSlice';
import { logout as logoutAction, login as loginAction } from '../../../../redux/auth/authSlice';
import { SnackTypes } from '../../../UtilityComponents/SnackBar/SnackBar';
import { ErrorLogger } from '../../../../utils/Logger';

const SOCKET_RECONNECT_DELAY_MS = 2000;

export enum UserStateEnum {
  Client,
  Freelancer,
  LoggedOut
}

interface ContactFormData {
  fullName: string;
  contactNo: string;
  email: string;
  message: string;
}

export const useNavbarController = (useScrollVersion?: Boolean, whiteText?: Boolean) => {
  // State management
  const [scrolling, setScrolling] = useState(useScrollVersion !== null ? useScrollVersion : false);
  const [useWhiteText, setUseWhiteText] = useState(whiteText !== undefined ? whiteText : false);
  const [showMenuPopup, setShowMenuPopup] = useState('none');
  const [openNotificationPanel, setOpenNotificationPanel] = useState(false);
  const [openFavPanel, setOpenFavPanel] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [navbarTopFiveNotifications, setNavbarTopFiveNotifications] = useState<NotificationInterface[]>([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState<number>(0);
  const [tab, setTab] = useState(0);
  const [formData, setFormData] = useState<ContactFormData>({
    fullName: "",
    contactNo: "",
    email: "",
    message: ""
  });

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // Get auth state from Redux
  const { isLoggedIn, buyer } = useSelector((state: RootState) => state.auth);
  const panelClosed = useSelector((state: RootState) => state.notification.panelClosed);
  const openModal = useSelector((state: RootState) => state.contactUsModal.footerModalOpen);
  // Use Redux for unread message count instead of local state
  const unreadMsgCount = useSelector((state: RootState) => state.chat.unreadCount);

  // Derive userState from Redux auth state
  const userState = !isLoggedIn 
    ? UserStateEnum.LoggedOut 
    : (buyer?.seller !== undefined ? UserStateEnum.Freelancer : UserStateEnum.Client);

  const snackBarRef = useRef<{ showSnackbar(): any }>(null);
  const [snackBarParams, setSnackBarParams] = useState({
    message: "",
    state: SnackTypes.error
  });

  // Snackbar handler
  const showSnackbar = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message: message, state: state });
    snackBarRef.current?.showSnackbar();
  };

  // Sign out handler
  const performSignout = () => {
    SessionManager.shared.signOut();
    dispatch(logoutAction());
    navigate("/");
  };

  // Menu popup handler
  const menuPopupHandler = (menu: string) => {
    if (menu === "notification") {
      if (unreadNotificationCount === 0) {
        setShowMenuPopup("none");
        setOpenNotificationPanel(true);
      } else {
        if (openNotificationPanel) {
          setOpenNotificationPanel(false);
          setShowMenuPopup("none");
        } else {
          setShowMenuPopup(menu);
        }
      }
    } else if (showMenuPopup === menu) {
      setShowMenuPopup("none");
    } else {
      setShowMenuPopup(menu);
    }
  };

  // Favourites panel handler
  const handleFavouritesClick = () => {
    if (!openFavPanel) {
      setOpenNotificationPanel(false);
      setOpenFavPanel(true);
    } else {
      setOpenFavPanel(false);
    }
  };

  // Notification panel handler
  const handleNotificationClick = () => {
    setShowMenuPopup('none');
    if (openFavPanel) setOpenFavPanel(false);
    setOpenNotificationPanel(true);
  };

  // Order navigation handler
  const handleOrderClick = () => {
    navigate("/my-orders");
  };

  // Contact form handlers
  const resetFormData = () => setFormData({
    fullName: "",
    contactNo: "",
    email: "",
    message: ""
  });

  const handleClose = () => {
    resetFormData();
    dispatch(closeContactUsModal());
  };

  const handleContactUsClick = () => {
    navigate("/contact-us");
  };

  const handleFormData = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const fieldName = e.target.name;
    const val = e.target.value;
    setFormData({ ...formData, [fieldName]: val });
  };

  const submitFeedback = async () => {
    handleClose();
    setShowLoader(true);
    try {
      const success = await FeedbackAPIHandler.shared.writeToUsFeedback(formData);
      if (success) {
        showSnackbar("Feedback submitted successfully!", SnackTypes.success);
      } else {
        throw new Error("Failed to submit feedback.");
      }
    } catch (error) {
      showSnackbar(error as string, SnackTypes.error);
    } finally {
      setShowLoader(false);
    }
  };

  // Scroll handler
  const changeNavbarColor = () => {
    if (useScrollVersion == null) {
      if (window.scrollY >= 10) {
        setScrolling(true);
      } else {
        setScrolling(false);
      }
    }

    if (whiteText !== undefined) {
      if (window.scrollY >= 10) {
        setUseWhiteText(false);
      } else {
        setUseWhiteText(true);
      }
    }
  };

  const scrollTop = () => {
    window.scrollTo(0, 0);
  };

  // Mobile navbar handlers
  const toggleMobileBar = () => {
    document.querySelector('#menuIcon')?.classList.toggle('fa-times');
    setShowMenuPopup('none');
  };

  const toggleMobileContent = () => {
    document.querySelector('.mobileNavbarContent')?.classList.toggle('active');
  };

  const toggleMobileList = () => {
    document.querySelector('.mobileNavbar-list')?.classList.toggle('active');
  };

  const mobileNaveStateToggle = () => {
    const isOpening = !document.querySelector('.mobileNavbarContent')?.classList.contains('active');
    
    toggleMobileBar();
    toggleMobileContent();
    toggleMobileList();
    
    if (isOpening) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
  };

  // Update notifications for navbar
  const updateNotificationForNavbar = async () => {
    try {
      const notificationsForNavBar = await NotificationAPIHandler.shared.getNavbarNotifications();
      setUnreadNotificationCount(notificationsForNavBar.totalUnreadCount);
      setNavbarTopFiveNotifications(notificationsForNavBar.unreadNotifications);
    } catch (error) {
      ErrorLogger.log(error, 'useNavbarController.fetchNotifications');
    }
  };

  // Setup socket connection for real-time notifications, returns a cleanup function
  const setupSocketConnection = (userId: string) => {
    if (!SessionManager.shared.fetchJWTToken()) return undefined;

    const socket = io(ServerConfig.shared.baseDomainName, {
      // Read per attempt so reconnects always send the current token.
      auth: (sendAuth: (data: object) => void) =>
        sendAuth({ token: SessionManager.shared.fetchJWTToken() })
    });

    const handleNewNotification = (notification: NotificationInterface) => {
      showSnackbar(`New Notification: ${notification.title}`, SnackTypes.info);

      const notificationSound = new Audio('/assets/sounds/gg_notification_default.mp3');
      notificationSound.play().catch(() => {});

      dispatch(triggerNewNotification());
      updateNotificationForNavbar();
    };

    // Server derives the room from the token, subscribe is kept for backwards compatibility
    socket.on('connect', () => {
      socket.emit('subscribe', userId);
    });

    socket.on('new-notification', handleNewNotification);

    socket.on('connect_error', (error) => {
      ErrorLogger.log(error, 'useNavbarController.notificationSocket');
    });

    // socket.io never retries a server-initiated disconnect, so without this the
    // socket stays silently dead and no notification ever arrives again.
    let reconnectTimer: number | undefined;
    const handleDisconnect = (reason: string) => {
      if (reason !== 'io server disconnect') return;
      if (!SessionManager.shared.fetchJWTToken()) return;
      reconnectTimer = window.setTimeout(() => socket.connect(), SOCKET_RECONNECT_DELAY_MS);
    };

    socket.on('disconnect', handleDisconnect);

    return () => {
      window.clearTimeout(reconnectTimer);
      socket.off('new-notification', handleNewNotification);
      socket.off('disconnect', handleDisconnect);
      socket.disconnect();
    };
  };

  // Initialize user data and connections
  useEffect(() => {
    const getUserData = async () => {
      try {
        let buyerData = SessionManager.shared.buyer || await BuyerAPIHandler.shared.getBuyerByAuthToken();
        SessionManager.shared.buyer = buyerData;
        
        // Dispatch login action to Redux
        dispatch(loginAction(buyerData));

        setShowLoader(false);

        Clarity.identify(buyerData.id);
      } catch (err) {
        setShowLoader(false);
      }
    };

    getUserData();
    updateNotificationForNavbar();
    // eslint-disable-next-line 
  }, []);

  // Notification socket, recreated whenever the session changes
  useEffect(() => {
    if (!isLoggedIn || !buyer?.id) return;

    const cleanupSocket = setupSocketConnection(buyer.id);
    return () => {
      cleanupSocket?.();
    };
    // eslint-disable-next-line 
  }, [isLoggedIn, buyer?.id]);

  // Handle panel closed state
  useEffect(() => {
    if (panelClosed) {
      updateNotificationForNavbar();
      dispatch(resetPanelClosed());
    }
    // eslint-disable-next-line 
  }, [panelClosed]);

  // Setup scroll listener
  useEffect(() => {
    window.addEventListener('scroll', changeNavbarColor);
    return () => {
      window.removeEventListener('scroll', changeNavbarColor);
    };
    // eslint-disable-next-line
  }, []);

  return {
    // State
    scrolling,
    useWhiteText,
    userState,
    showMenuPopup,
    openNotificationPanel,
    setOpenNotificationPanel,
    openFavPanel,
    setOpenFavPanel,
    showLoader,
    buyer,
    navbarTopFiveNotifications,
    unreadNotificationCount,
    unreadMsgCount,
    tab,
    setTab,
    formData,
    openModal,
    snackBarRef,
    snackBarParams,
    location,
    
    // Handlers
    performSignout,
    menuPopupHandler,
    handleFavouritesClick,
    handleNotificationClick,
    handleOrderClick,
    handleContactUsClick,
    handleFormData,
    submitFeedback,
    handleClose,
    scrollTop,
    mobileNaveStateToggle,
    showSnackbar,
    navigate,
  };
};
